import { Dialog, Text, Stack, theme, OverlaySpinner } from '@prophecy/ui';
import { XCloseIcon, CheckIcon } from '@prophecy/ui-v3/Icons';
import { MessageDetail } from '@prophecy/ui-v3/LegacyToast';
import styled from 'styled-components';

import { useGraphQlMutation } from '../../data/util';
import { graphql } from '../../graphqlTypes/graphql';

const ErrorIcon = styled(XCloseIcon)`
  color: ${theme.colors.error500};
`;

const SuccessIcon = styled(CheckIcon)`
  color: ${theme.colors.success500};
`;
const query = graphql(`
  query checkGitTokenValidity($projectUID: String!) {
    checkGitTokenValidity(projectUID: $projectUID) {
      readAccess
      writeAccess
      diagnostics
    }
  }
`);
function Access({ readAccess, writeAccess }: { readAccess: boolean; writeAccess: boolean }) {
  return (
    <Stack direction='horizontal' alignY='center' align='space-between' padding={`0 ${theme.spaces.x4}`}>
      <Stack direction='horizontal' alignY='center' gap={theme.spaces.x4}>
        <Text level='sm'>Read Access: </Text>
        {readAccess ? <SuccessIcon size='m' type='default' /> : <ErrorIcon size='m' type='default' />}
      </Stack>
      <Stack direction='horizontal' alignY='center' gap={theme.spaces.x4}>
        <Text level='sm'>Write Access: </Text>
        {writeAccess ? <SuccessIcon size='m' type='default' /> : <ErrorIcon size='m' type='default' />}
      </Stack>
    </Stack>
  );
}

export function useCheckGitTokenValidity() {
  const { mutateAsync, isLoading } = useGraphQlMutation(query);
  async function checkGitTokenValidity(projectUID: string) {
    OverlaySpinner.show({ tip: 'Validating git credentials...' });

    const resp = await mutateAsync({ projectUID });
    OverlaySpinner.hide();
    const { diagnostics, writeAccess, readAccess } = resp.checkGitTokenValidity;
    if (diagnostics) {
      Dialog.alert({
        'data-test-id': 'git-access-alert-dialog',
        title: <Text level='sm'>Git Access</Text>,
        children: (
          <Stack gap={theme.spaces.x12}>
            <Access readAccess={readAccess} writeAccess={writeAccess} />
            <MessageDetail content={diagnostics} />
          </Stack>
        ),
        size: 'l',
        iconPlacement: 'right',
        closeButton: { children: 'Close' },
        okButton: null
      });
      return;
    } else {
      Dialog.info({
        'data-test-id': 'git-access-info-dialog',
        title: <Text level='sm'>Git Access</Text>,
        children: <Access readAccess={readAccess} writeAccess={writeAccess} />,
        size: 'l',
        iconPlacement: 'right',
        closeButton: { children: 'Close' },
        okButton: null
      });
      return;
    }
  }
  return { checkGitTokenValidity, isLoading };
}
